"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import SupportCard from "@/app/(landing)/mental-health/components/support-card";
import { motion } from "framer-motion";

/**
 * Props for `OurServicesSection`.
 */
export type OurServicesSectionProps =
  SliceComponentProps<Content.OurServicesSectionSlice>;

/**
 * Component for "OurServicesSection" Slices.
 */
const OurServicesSection: FC<OurServicesSectionProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === "default" ? (
        <div className="bg-[#F8FCFF]">
          <Bounded className="py-20">
            {/* heading */}

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <PrismicRichText
                field={slice.primary.heading}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="text-primary text-4xl font-bold lg:text-5xl lg:font-semibold text-center">
                      {children}
                    </h1>
                  ),
                  heading2: ({ children }) => (
                    <h2 className="text-primary text-4xl font-bold lg:text-5xl lg:font-semibold text-center">
                      {children}
                    </h2>
                  ),
                  heading3: ({ children }) => (
                    <h3 className="text-primary text-4xl font-bold lg:text-5xl lg:font-semibold text-center">
                      {children}
                    </h3>
                  ),
                  heading4: ({ children }) => (
                    <h4 className="text-primary text-4xl font-bold lg:text-5xl lg:font-semibold text-center">
                      {children}
                    </h4>
                  ),
                  heading5: ({ children }) => (
                    <h5 className="text-primary text-4xl font-bold lg:text-5xl lg:font-semibold text-center">
                      {children}
                    </h5>
                  ),
                  heading6: ({ children }) => (
                    <h6 className="text-primary text-4xl font-bold lg:text-5xl lg:font-semibold text-center">
                      {children}
                    </h6>
                  ),
                  label: ({ node, children }) => {
                    if (node.data.label === "Highlight") {
                      return <span className="text-secondary">{children}</span>;
                    }
                  },
                }}
              />
            </motion.div>

            {/* cards */}

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              {slice.primary.service_cards.map((item, index) => (
                <SupportCard key={index} data={item} />
              ))}
            </motion.div>
          </Bounded>
        </div>
      ) : null}
    </section>
  );
};

export default OurServicesSection;
